<template>
  <div class="grid gap-x-4 gap-y-4 md:grid-cols-3">
    <div class="md:col-span-2">
      <h4>{{ t('vehicle') }}</h4>
      <div class="grid min-h-max items-start gap-4 md:flex">
        <div class="basis-[55%]">
          <img :src="imageUrl" class="w-full" />
        </div>
        <div class="mt-2 h-min basis-[45%] flex-col items-start space-y-1 md:flex">
          <p class="text-sm font-medium lg:text-base">
            {{ car.vehicleType?.make.name }}
            {{ car.vehicleType?.model }}
          </p>
          <LicencePlate>{{ car.licencePlate }}</LicencePlate>
          <p class="font-normal">
            <template v-if="!car.isGhostCar">FIN: {{ car.fin }} </template>{{ car.fleetNumber }}
          </p>
        </div>
      </div>
    </div>

    <div class="h-min md:col-start-3">
      <div class="-mb-2 flex flex-col space-y-2">
        <LabeledText :label="startDateLabelResolved">
          {{ formattedStartDate }}
        </LabeledText>
        <div v-if="!hideLocations">
          <p v-if="bookingOrAllocation.pickupLocationType === BookingLocationType.STATION">
            {{ handoverPlaceLabel('pickup') }}
          </p>
          <div v-else>
            <p v-if="!isFetchingPickupLocation">
              {{
                bookingOrAllocation.pickupLocationGeocodedAddress?.displayName ??
                pickupLocationData?.address?.displayName
              }}
            </p>
            <div v-else class="flex justify-center"><Spinner /></div>
          </div>
        </div>
      </div>

      <Divider />

      <div class="-mt-2 flex flex-col space-y-2">
        <LabeledText :label="endDateLabelResolved">
          <span>{{ formattedEndDate }}</span>
        </LabeledText>
        <div v-if="!hideLocations">
          <p v-if="bookingOrAllocation.dropoffLocationType === BookingLocationType.STATION">
            {{ handoverPlaceLabel('dropoff') }}
          </p>
          <div v-else>
            <p v-if="!isFetchingDropoffLocation">
              {{
                bookingOrAllocation.dropoffLocationGeocodedAddress?.displayName ??
                dropoffLocationData?.address?.displayName
              }}
            </p>
            <div v-else class="flex justify-center"><Spinner /></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Divider from '@/components/Divider.vue';
import LabeledText from '@/components/LabeledText.vue';
import LicencePlate from '@/components/LicencePlate.vue';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useLocationsData } from '@/hooks/use-location-data';
import { pointToLocationDto } from '@/hooks/use-location-to-coordinates';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import type { Car } from '@/entities/cars/car.entity.ts';
const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const props = defineProps<{
  bookingOrAllocation: Booking | Allocation;
  car: Car;
  hideLocations?: boolean;
  /** When set, replaces the default "Mietbeginn" / start-of-rental label (e.g. workshop handover). */
  startDateLabel?: string;
  /** When set, replaces the default "Mietende" / end-of-rental label (e.g. workshop handover). */
  endDateLabel?: string;
}>();

const startDateLabelResolved = computed(() => props.startDateLabel ?? t('startDate'));
const endDateLabelResolved = computed(() => props.endDateLabel ?? t('endDate'));

const formattedStartDate = useFormattedDate(props.bookingOrAllocation.startDate);
const formattedEndDate = useFormattedDate(props.bookingOrAllocation.endDate);

const isAllocation = (item: Booking | Allocation): item is Allocation =>
  'pickupResponsibleStation' in item;

const handoverPlaceLabel = (mode: 'pickup' | 'dropoff') => {
  const item = props.bookingOrAllocation;
  if (isAllocation(item)) {
    return (
      item[`${mode}LocationGeocodedAddress`]?.displayName ||
      getLocalizedField(item[`${mode}ResponsibleStation`]?.info)
    );
  }
  return getLocalizedField(item[`${mode}Station`]?.info);
};

const imageUrl = computed(() => props.car.imageUrl ?? props.car.vehicleType.imageUrl);

const {
  pickupLocationData,
  isFetchingPickupLocation,
  dropoffLocationData,
  isFetchingDropoffLocation,
} = useLocationsData(
  computed(() => ({
    pickupLocationType: props.bookingOrAllocation.pickupLocationType,
    dropoffLocationType: props.bookingOrAllocation.dropoffLocationType,
    pickupLocation: pointToLocationDto(props.bookingOrAllocation.pickupLocation),
    dropoffLocation: pointToLocationDto(props.bookingOrAllocation.dropoffLocation),
    pickupLocationGeocodedAddress: props.bookingOrAllocation.pickupLocationGeocodedAddress,
    dropoffLocationGeocodedAddress: props.bookingOrAllocation.dropoffLocationGeocodedAddress,
  })),
);
</script>

<i18n lang="json">
{
  "en": {
    "customerComment": "Customer Comment",
    "returnOverview": "Return overview",
    "renter": "Renter",
    "bookingNumber": "Booking Nr.",
    "vehicle": "Vehicle",
    "startDate": "Start of rental",
    "endDate": "End of rental"
  },
  "de": {
    "customerComment": "Kundenkommentar",
    "returnOverview": "Buchungsübersicht",
    "renter": "Mieter",
    "bookingNumber": "Buchungs Nr.",
    "vehicle": "Fahrzeug",
    "startDate": "Mietbeginn",
    "endDate": "Mietende"
  },
  "es": {
    "customerComment": "Comentario del cliente",
    "returnOverview": "Resumen de la devolución",
    "renter": "Arrendatario",
    "bookingNumber": "N.º de reserva",
    "vehicle": "Vehículo",
    "startDate": "Inicio del alquiler",
    "endDate": "Fin del alquiler"
  }
}
</i18n>
